// src/pages/DocumentLanding.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import docusealService from '../services/docusealService';
import SigningSetupModal from '../components/SigningSetupModal';
import SigningProgressModal from '../components/SigningProgressModal';
import DrawingViewer from '../components/DrawingViewer';
import FolderManagementModal from '../components/FolderManagementModal';
import AddTeamsModal from '../components/AddTeamsModal';
import withProjectPermissions from '../components/withProjectPermissions';
import { ArrowLeft, Folder, FolderPlus, Archive, Pencil, Check } from 'lucide-react';
import './DocumentLanding.css';

const DEFAULT_DOCUMENT_CONFIG = {
  sortBy: 'uploadedAt',
  sortOrder: 'desc',
  folders: []
};

/** Who must sign for team sign-off (live roster; same rules as SigningProgressModal). */
function buildTeamSignOffSignerIds(teamsList, projectData, teamAssignment, selectedTeamIds) {
  const memberIds = new Set();
  const relevantTeams = teamAssignment === 'all'
    ? (teamsList || [])
    : (teamsList || []).filter(t => (selectedTeamIds || []).includes(t.id));
  for (const team of relevantTeams) {
    for (const m of team.members || []) {
      if (m?.id) memberIds.add(m.id);
    }
  }
  if (projectData) {
    if (projectData.createdBy) memberIds.add(projectData.createdBy);
    const collab = projectData.collaborators || {};
    for (const [userId, data] of Object.entries(collab)) {
      if (data?.status === 'accepted' && data?.role === 'pm_collaborator') memberIds.add(userId);
    }
  }
  return memberIds;
}

function computeTeamSignOffTotalSigners(teamsList, projectData, teamAssignment, selectedTeamIds) {
  return buildTeamSignOffSignerIds(teamsList, projectData, teamAssignment, selectedTeamIds).size;
}

function getTeamSignOffProgress(doc, teamsList, projectData) {
  if (!doc || doc.mode !== 'team_sign_off') return { signed: 0, total: 0 };
  const ids = buildTeamSignOffSignerIds(teamsList, projectData, doc.teamAssignment, doc.selectedTeams);
  const acks = doc.acknowledgements || {};
  let signed = 0;
  for (const id of ids) {
    if (acks[id]) signed += 1;
  }
  return { signed, total: ids.size };
}

function DocumentLanding({ permissions }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, userLoading] = useAuthState(auth);
  const [project, setProject] = useState(null);
  const [teams, setTeams] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSigningSetupModal, setShowSigningSetupModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedDocumentForProgress, setSelectedDocumentForProgress] = useState(null);
  const [viewer, setViewer] = useState({ open: false, file: null });
  const [loading, setLoading] = useState(true);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSettingUpSigning] = useState(false);
  // Removed unused state variables: refreshing, refreshingDocuments

  // Document upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('all');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [addTeamsDocument, setAddTeamsDocument] = useState(null);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [editingDocumentName, setEditingDocumentName] = useState('');
  const [renamingDocumentId, setRenamingDocumentId] = useState(null);
  const [uploadFolderId, setUploadFolderId] = useState(null);
  const [teamAssignment, setTeamAssignment] = useState('all'); // 'all' | 'selected' - for Team sign-off
  const [teamSignOffSelectedTeamIds, setTeamSignOffSelectedTeamIds] = useState([]); // for teamAssignment === 'selected'
  const [mandatoryBeforeSignIn, setMandatoryBeforeSignIn] = useState(false);
  const [signOffTitle, setSignOffTitle] = useState('');
  const [signOffTemplate, setSignOffTemplate] = useState('basic'); // 'basic' | 'detailed' | 'full_induction'
  const [uploadTab, setUploadTab] = useState('rams_and_permits'); // which tab's upload flow to show in modal
  const isMountedRef = useRef(true);
  const documentsRef = useRef([]);

  // Closed projects and non-editors (viewers, company owners without collaborator status)
  // are both read-only here, just for different reasons.
  const isReadOnly = project?.status === 'closed' || !permissions?.canEdit;

  // Utility function to preserve API data when updating documents
  const preserveApiData = (newDoc, prevDoc) => {
    if (!prevDoc || !newDoc.docusealSubmissionId) return newDoc;
    
    // If the new doc doesn't have API data but the previous one does, preserve it
    if (!newDoc.docusealSubmitters && prevDoc.docusealSubmitters) {
      console.log('Preserving API data for document:', newDoc.id);
      return {
        ...newDoc,
        docusealSubmitters: prevDoc.docusealSubmitters,
        signingProgress: prevDoc.signingProgress,
        completedSigners: prevDoc.completedSigners,
        totalSigners: prevDoc.totalSigners,
        signingStatus: prevDoc.signingStatus
      };
    }
    
    // If both have API data, merge them to preserve any new signing information
    if (newDoc.docusealSubmitters && prevDoc.docusealSubmitters) {
      console.log('Merging API data for document:', newDoc.id);
      
      // Create a map of previous submitters for quick lookup
      const prevSubmittersMap = new Map(
        prevDoc.docusealSubmitters.map(s => [s.email, s])
      );
      
      // Merge submitters, preferring new data but preserving completed status
      const mergedSubmitters = newDoc.docusealSubmitters.map(newSubmitter => {
        const prevSubmitter = prevSubmittersMap.get(newSubmitter.email);
        if (prevSubmitter && prevSubmitter.status === 'completed') {
          // Preserve completed status from previous data
          return { ...newSubmitter, status: 'completed' };
        }
        return newSubmitter;
      });
      
      return {
        ...newDoc,
        docusealSubmitters: mergedSubmitters,
        // Recalculate progress based on merged data
        completedSigners: mergedSubmitters.filter(s => s.status === 'completed').length,
        totalSigners: mergedSubmitters.length,
        signingProgress: mergedSubmitters.length > 0 
          ? mergedSubmitters.filter(s => s.status === 'completed').length / mergedSubmitters.length 
          : prevDoc.signingProgress
      };
    }
    
    return newDoc;
  };

  const documentConfig = useMemo(() => {
    const raw = { ...DEFAULT_DOCUMENT_CONFIG, ...(project?.documentConfig || {}) };
    if (raw.sortBy === 'status') {
      return { ...raw, sortBy: 'uploadedAt' };
    }
    return raw;
  }, [project?.documentConfig]);

  const saveDocumentConfig = useCallback(async (updates) => {
    if (!projectId) return;
    const next = { ...documentConfig, ...updates };
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        documentConfig: next
      });
      setProject(prev => prev ? { ...prev, documentConfig: next } : null);
    } catch (err) {
      console.error('Failed to save document config:', err);
      alert('Failed to save. Please try again.');
    }
  }, [projectId, documentConfig]);

  const addFolder = useCallback(async (name) => {
    const id = crypto.randomUUID?.() || `folder-${Date.now()}`;
    const folders = [...(documentConfig.folders || []), { id, name, order: (documentConfig.folders?.length || 0) }];
    await saveDocumentConfig({ folders });
  }, [documentConfig.folders, saveDocumentConfig]);

  const renameFolder = useCallback(async (folderId, name) => {
    const folders = (documentConfig.folders || []).map(f =>
      f.id === folderId ? { ...f, name } : f
    );
    await saveDocumentConfig({ folders });
  }, [documentConfig.folders, saveDocumentConfig]);

  const deleteFolder = useCallback(async (folderId) => {
    if (!window.confirm('Delete this folder? Documents will move to Uncategorised.')) return false;
    const folders = (documentConfig.folders || []).filter(f => f.id !== folderId);
    const docsInFolder = documents.filter(d => d.folderId === folderId);
    await saveDocumentConfig({ folders });
    for (const d of docsInFolder) {
      try {
        await updateDoc(doc(db, `projects/${projectId}/documents/${d.id}`), { folderId: null });
      } catch (e) {
        console.error('Failed to move doc to uncategorised:', e);
      }
    }
    if (selectedFolderId === folderId) setSelectedFolderId('all');
    return true;
  }, [documentConfig.folders, documents, projectId, selectedFolderId, saveDocumentConfig]);

  const moveDocumentToFolder = useCallback(async (documentId, folderId) => {
    if (isReadOnly) return;
    try {
      await updateDoc(doc(db, `projects/${projectId}/documents/${documentId}`), {
        folderId: folderId || null
      });
    } catch (e) {
      console.error('Failed to move document:', e);
      alert('Failed to move document.');
    }
  }, [projectId, isReadOnly]);

  const cancelDocumentRename = useCallback(() => {
    setEditingDocumentId(null);
    setEditingDocumentName('');
  }, []);

  const startDocumentRename = useCallback((document) => {
    if (isReadOnly) return;
    setEditingDocumentId(document.id);
    setEditingDocumentName(document.name || '');
  }, [isReadOnly]);

  const renameDocument = useCallback(async (documentId, rawName) => {
    if (!projectId) return false;
    if (isReadOnly) {
      cancelDocumentRename();
      return false;
    }

    const nextName = rawName?.trim() || '';
    const currentDocument = documents.find(d => d.id === documentId);
    if (!currentDocument) {
      cancelDocumentRename();
      return false;
    }

    const currentName = (currentDocument.name || '').trim();
    if (!nextName) {
      alert('Please enter a document name.');
      return false;
    }

    if (nextName === currentName) {
      cancelDocumentRename();
      return true;
    }

    const existingDocument = documents.find(d => d.id !== documentId && d.name === nextName);
    if (existingDocument) {
      alert(`A document with the name "${nextName}" already exists. Please use a different name or archive the existing document first.`);
      return false;
    }

    try {
      setRenamingDocumentId(documentId);
      await updateDoc(doc(db, `projects/${projectId}/documents/${documentId}`), {
        name: nextName
      });

      setDocuments(prev => {
        const nextDocuments = prev.map(d => d.id === documentId ? { ...d, name: nextName } : d);
        documentsRef.current = nextDocuments;
        return nextDocuments;
      });
      setSelectedDocument(prev => prev?.id === documentId ? { ...prev, name: nextName } : prev);
      setSelectedDocumentForProgress(prev => prev?.id === documentId ? { ...prev, name: nextName } : prev);
      cancelDocumentRename();
      return true;
    } catch (error) {
      console.error('Error renaming document:', error);
      alert('Error renaming document. Please try again.');
      return false;
    } finally {
      setRenamingDocumentId(null);
    }
  }, [cancelDocumentRename, documents, isReadOnly, projectId]);

  const hasArchivedDocuments = useMemo(() => documents.some(d => d.status === 'archived'), [documents]);

  const filteredAndSortedDocuments = useMemo(() => {
    const isArchivedView = selectedFolderId === 'archived';
    const visible = isArchivedView
      ? documents.filter(d => d.status === 'archived')
      : documents.filter(d => d.status !== 'archived');
    let filtered = visible;
    if (selectedFolderId === 'all' || selectedFolderId === 'archived') {
      filtered = visible;
    } else if (selectedFolderId === 'uncategorised') {
      filtered = visible.filter(d => !d.folderId);
    } else {
      filtered = visible.filter(d => d.folderId === selectedFolderId);
    }
    const cfg = documentConfig;
    const getDate = (d) => {
      const t = d.uploadedAt;
      if (!t) return 0;
      return t?.toMillis?.() ?? t?.toDate?.()?.getTime?.() ?? new Date(t).getTime();
    };
    const statusOrder = { draft: 0, sent: 1, completed: 2 };
    const getProgress = (d) => {
      if (d.mode === 'team_sign_off') {
        const { signed, total } = getTeamSignOffProgress(d, teams, project);
        return total > 0 ? (signed / total) * 100 : 0;
      }
      return (d.signingProgress ?? 0) * 100;
    };
    return [...filtered].sort((a, b) => {
      if (cfg.sortBy === 'name') {
        const cmp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        return cfg.sortOrder === 'asc' ? cmp : -cmp;
      }
      if (cfg.sortBy === 'uploadedAt') {
        const cmp = getDate(a) - getDate(b);
        return cfg.sortOrder === 'asc' ? cmp : -cmp;
      }
      if (cfg.sortBy === 'status') {
        const cmp = (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
        return cfg.sortOrder === 'asc' ? cmp : -cmp;
      }
      if (cfg.sortBy === 'signingProgress') {
        const cmp = getProgress(a) - getProgress(b);
        return cfg.sortOrder === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [documents, selectedFolderId, documentConfig, teams, project]);

  // Fetch documents separately for easier refresh
  const fetchDocuments = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const docsSnap = await getDocs(collection(db, `projects/${projectId}/documents`));
      const docsData = docsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Fetched documents from Firestore:', docsData);
      
      // DocuSeal sunset: use Firestore data only - no API calls for legacy DocuSeal docs
      setDocuments(docsData);
      documentsRef.current = docsData;
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [projectId]);

  // Real-time listener for document changes (from webhooks)
  useEffect(() => {
    if (!projectId) return;

    console.log('Setting up real-time listener for documents');
    
    const unsubscribe = onSnapshot(
      collection(db, `projects/${projectId}/documents`),
      (snapshot) => {
        const docsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Real-time document update:', docsData);
        
        // Only update if there are actual changes to avoid infinite loops
        setDocuments(prev => {
                  // Skip updates during archiving or signing setup to prevent interference
        if (isArchiving || isSettingUpSigning) {
          console.log('Skipping real-time update during archiving or signing setup');
          return prev;
        }
          
          // Deep comparison to check for actual changes
          const hasChanges = JSON.stringify(prev) !== JSON.stringify(docsData);
          if (hasChanges) {
            console.log('Documents updated via real-time listener');
            // Ensure we're not losing any API data that was fetched
            const updatedDocsData = docsData.map(doc => {
              const prevDoc = prev.find(p => p.id === doc.id);
              return preserveApiData(doc, prevDoc);
            });
            documentsRef.current = updatedDocsData;
            return updatedDocsData;
          }
          return prev;
        });
      },
      (error) => {
        console.error('Error in real-time listener:', error);
      }
    );

    return () => {
      console.log('Cleaning up real-time listener');
      unsubscribe();
    };
  }, [projectId, isArchiving, isSettingUpSigning]);

  // Update signing progress for sent documents
  const updateSigningProgress = useCallback(async () => {
    if (!projectId || !isMountedRef.current) return;
    // DocuSeal sunset: skip API calls - use Firestore data only for legacy DocuSeal docs
    return;
  }, [projectId]);

  // Update progress every 30 seconds for sent documents and immediately on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Check if there are any documents with submission IDs
    const hasDocumentsWithSubmission = documentsRef.current.some(doc => doc.docusealSubmissionId);
    if (!hasDocumentsWithSubmission) return;
    
    // Update immediately on mount
    updateSigningProgress();
    
    // Then update every 30 seconds to reduce API calls
    const interval = setInterval(updateSigningProgress, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [updateSigningProgress]);

  // More frequent updates for documents with recent activity (every 10 seconds)
  useEffect(() => {
    const hasActiveDocuments = documentsRef.current.some(doc => 
      doc.docusealSubmissionId && doc.signingStatus === 'active'
    );
    
    if (!hasActiveDocuments) return;
    
    const activeInterval = setInterval(updateSigningProgress, 10000); // 10 seconds
    return () => clearInterval(activeInterval);
  }, [updateSigningProgress]);

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          setProject({ id: projectDoc.id, ...projectDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };
    fetchProject();
  }, [projectId]);

  // Fetch teams and documents
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      setLoading(true);
      
      try {
        // Fetch teams
        const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teamsData = [];

        for (const teamDoc of teamsSnap.docs) {
          const team = { id: teamDoc.id, ...teamDoc.data(), members: [] };
          
          // Fetch members for this team
          const membersSnap = await getDocs(
            collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`)
          );

          for (const memberDoc of membersSnap.docs) {
            const memberData = memberDoc.data();
            const memberId = memberDoc.id;
            
            console.log(`🔍 Team ${team.name} - Member ${memberId}:`, {
              name: memberData.name,
              email: memberData.email,
              phoneNumber: memberData.phoneNumber,
              authMethod: memberData.authMethod,
              fullData: memberData
            });

            // Determine auth method based on available data
            let authMethod = memberData.authMethod;
            if (!authMethod) {
              // If no authMethod stored, infer from available data
              if (memberData.phoneNumber && memberData.phoneNumber.trim() !== '' && (!memberData.email || memberData.email.trim() === '')) {
                authMethod = 'phone';
              } else {
                authMethod = 'email';
              }
            }

            // Fetch profile picture from global_backup (single source of truth)
            let photoURL = null;
            try {
              const globalProfileDoc = await getDoc(doc(db, `global_backup/users/profiles/${memberId}`));
              if (globalProfileDoc.exists()) {
                photoURL = globalProfileDoc.data()?.profilePictureUrl || null;
              }
            } catch (error) {
              console.log(`Could not fetch profile picture for ${memberId}:`, error);
            }

            team.members.push({
              id: memberId,
              name: memberData.name || 'Unknown',
              email: memberData.email || '',
              phone: memberData.phoneNumber || '',
              authMethod: authMethod,
              photoURL: photoURL,
              teamId: team.id,
              teamName: team.name,
              teamColor: team.color
            });
          }

          teamsData.push(team);
        }

        setTeams(teamsData);
        await fetchDocuments();

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, fetchDocuments]);

  // Fetch current user information
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!user) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUser({
            id: user.uid,
            ...userData
          });
          console.log('Current user loaded:', userData);
        } else {
          console.log('Current user not found in Firestore');
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        setCurrentUser(null);
      }
    };

    fetchCurrentUser();
  }, [user]);

  // Cleanup effect to prevent updates after unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoize the file URL to prevent unnecessary iframe refreshes
  const filePreviewUrl = useMemo(() => {
    if (uploadFile) {
      return URL.createObjectURL(uploadFile);
    }
    return null;
  }, [uploadFile]);

  // Cleanup object URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  // Helper: check if file is PDF or Word (doc/docx) - for team sign-off
  const isAllowedDocumentType = (file) => {
    const name = file.name.toLowerCase();
    const allowedTypes = [
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' // .docx
    ];
    return allowedTypes.includes(file.type) || name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx');
  };

  // Helper: allowed file types for general documentation (PDF, Word only – same as RAMS)
  const isAllowedForGeneralRecord = (file) => isAllowedDocumentType(file);

  // Handle file selection (preview mode)
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowed = uploadTab === 'general_record' ? isAllowedForGeneralRecord(file) : isAllowedDocumentType(file);
      if (!allowed) {
        alert('Only PDF and Word documents (.pdf, .doc, .docx) are allowed.');
        e.target.value = '';
        return;
      }
      setUploadFile(file);
      setDocumentName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension
    }
    e.target.value = '';
  };

  // Handle document upload
  const handleUploadDocument = async () => {
    if (isReadOnly) return;
    if (!uploadFile || !documentName) {
      alert('Please fill in all required fields');
      return;
    }

    if (!isAllowedDocumentType(uploadFile)) {
      alert('Only PDF and Word documents (.pdf, .doc, .docx) are allowed for document signing.');
      return;
    }

    // Validation: need at least one team when using "selected" assignment
    if (teamAssignment === 'selected' && teamSignOffSelectedTeamIds.length === 0) {
      alert('Please select at least one team for the document.');
      return;
    }

    // Check if document with same name already exists
    const existingDocument = documents.find(doc => doc.name === documentName);
    if (existingDocument) {
      alert(`A document with the name "${documentName}" already exists. Please use a different name or archive the existing document first.`);
      return;
    }

    setUploading(true);
    try {
      // Team sign-off flow: create doc, upload to original.pdf
      const selectedTeamsForDoc = teamAssignment === 'all' ? [] : teamSignOffSelectedTeamIds;
      const totalSigners = computeTeamSignOffTotalSigners(teams, project, teamAssignment, teamSignOffSelectedTeamIds);
      const docRef = await addDoc(collection(db, `projects/${projectId}/documents`), {
        name: documentName,
        fileName: uploadFile.name,
        fileSize: uploadFile.size,
        fileType: uploadFile.type,
        uploadedBy: user?.uid || 'unknown',
        uploadedAt: new Date(),
        projectId: projectId,
        folderId: uploadFolderId || null,
        mode: 'team_sign_off',
        documentType: 'rams_and_permits',
        status: 'pending_signatures',
        teamAssignment,
        selectedTeams: selectedTeamsForDoc,
        mandatoryBeforeSignIn,
        signOffTitle: signOffTitle.trim().slice(0, 60) || null,
        signOffTemplate,
        acknowledgements: {},
        totalSigners,
        signedCount: 0,
      });
      const documentId = docRef.id;

      // Use correct extension for storage (pdf, doc, or docx)
      const ext = (() => {
        const n = uploadFile.name.toLowerCase();
        if (n.endsWith('.docx')) return 'docx';
        if (n.endsWith('.doc')) return 'doc';
        return 'pdf';
      })();
      const fileRef = ref(storage, `projects/${projectId}/documents/${documentId}/original.${ext}`);
      await uploadBytes(fileRef, uploadFile);
      const downloadURL = await getDownloadURL(fileRef);

      await updateDoc(docRef, {
        storagePathOriginal: fileRef.fullPath,
        fileURL: downloadURL
      });

      if (mandatoryBeforeSignIn) {
        await updateDoc(doc(db, 'projects', projectId), {
          mandatoryRamsDocumentIds: arrayUnion(documentId)
        });
      }

      console.log('Document uploaded successfully');

      // Reset form
      setUploadFile(null);
      setDocumentName('');
      setUploadFolderId(null);
      setTeamAssignment('all');
      setTeamSignOffSelectedTeamIds([]);
      setMandatoryBeforeSignIn(false);
      setSignOffTitle('');
      setSignOffTemplate('basic');
      setShowUploadModal(false);
      
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  // Handle general documentation upload (record keeping only, no sign-off)
  const handleUploadGeneralDocument = async () => {
    if (isReadOnly) return;
    if (!uploadFile || !documentName) {
      alert('Please add a file and enter a document name');
      return;
    }
    if (!isAllowedForGeneralRecord(uploadFile)) {
      alert('Only PDF and Word documents (.pdf, .doc, .docx) are allowed.');
      return;
    }
    const existingDocument = documents.find(d => d.name === documentName);
    if (existingDocument) {
      alert(`A document with the name "${documentName}" already exists. Please use a different name or archive the existing document first.`);
      return;
    }
    setUploading(true);
    try {
      const fileToUpload = uploadFile;
      const fileName = uploadFile.name;
      const fileType = uploadFile.type;
      const docRef = await addDoc(collection(db, `projects/${projectId}/documents`), {
        name: documentName.trim(),
        fileName,
        fileSize: fileToUpload.size,
        fileType,
        uploadedBy: user?.uid || 'unknown',
        uploadedAt: new Date(),
        projectId: projectId,
        folderId: uploadFolderId || null,
        mode: 'general_record',
        documentType: 'general_record',
        status: 'record',
      });
      const documentId = docRef.id;
      const ext = (() => {
        const n = fileName.toLowerCase();
        if (n.endsWith('.docx')) return 'docx';
        if (n.endsWith('.doc')) return 'doc';
        if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'jpg';
        if (n.endsWith('.png')) return 'png';
        if (n.endsWith('.gif')) return 'gif';
        if (n.endsWith('.webp')) return 'webp';
        return 'pdf';
      })();
      const fileRef = ref(storage, `projects/${projectId}/documents/${documentId}/original.${ext}`);
      await uploadBytes(fileRef, fileToUpload);
      const downloadURL = await getDownloadURL(fileRef);
      await updateDoc(docRef, {
        storagePathOriginal: fileRef.fullPath,
        fileURL: downloadURL
      });
      setUploadFile(null);
      setDocumentName('');
      setUploadFolderId(null);
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading general document:', error);
      alert('Error uploading document');
    } finally {
      setUploading(false);
    }
  };

  // Handle document signing setup
  const handleSignDocument = async (document) => {
    if (isReadOnly) return;
    try {
      setSelectedDocument(document);
      
      // Get recipients for this document - use stored recipients if available
      const recipients = getRecipientsFromDocument(document);
      console.log('Recipients for signing setup:', recipients);
      console.log('Current user context:', currentUser);
      
      // Ensure PM context is properly set for recipients
      const recipientsWithPMContext = recipients.map(recipient => {
        // If this recipient is the current user, ensure isPM is set
        if (currentUser && recipient.id === currentUser.id) {
          return { ...recipient, isPM: true };
        }
        return recipient;
      });
      
      console.log('Recipients with PM context:', recipientsWithPMContext);
      
      if (recipientsWithPMContext.length === 0) {
        alert('No recipients found for this document. Please re-upload the document with recipients selected.');
        return;
      }
      
      setShowSigningSetupModal(true);
      
    } catch (error) {
      console.error('Error setting up document signing:', error);
      alert('Error setting up document signing: ' + error.message);
    }
  };



  // Handle document archiving
  const handleArchiveDocument = async (document) => {
    if (isReadOnly) return;
    if (!window.confirm(`Are you sure you want to archive "${document.name}"? This will move the document to archive. You can re-upload it later if needed.`)) {
      return;
    }

    try {
      setIsArchiving(true);
      // Archive the document in Firestore (soft delete)
      const docRef = doc(db, `projects/${projectId}/documents/${document.id}`);
      await updateDoc(docRef, {
        status: 'archived',
        archivedAt: new Date(),
        archivedBy: user?.uid || 'unknown'
      });

      if (document.mandatoryBeforeSignIn) {
        await updateDoc(doc(db, 'projects', projectId), {
          mandatoryRamsDocumentIds: arrayRemove(document.id)
        });
      }

      // Update local state - move to archived instead of removing
      setDocuments(prev => {
        const newDocuments = prev.map(doc => 
          doc.id === document.id 
            ? { ...doc, status: 'archived', archivedAt: new Date(), archivedBy: user?.uid || 'unknown' }
            : doc
        );
        documentsRef.current = newDocuments;
        console.log('Documents after archiving:', newDocuments.map(d => ({ 
          id: d.id, 
          name: d.name,
          status: d.status,
          docusealSubmitters: d.docusealSubmitters?.length || 0,
          completedSigners: d.completedSigners,
          totalSigners: d.totalSigners
        })));
        return newDocuments;
      });
      
      alert('Document archived successfully');
      
    } catch (error) {
      console.error('Error archiving document:', error);
      alert('Error archiving document: ' + error.message);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleUnarchiveDocument = async (document) => {
    if (isReadOnly) return;
    if (!window.confirm(`Restore "${document.name}" to the main document list?`)) return;
    try {
      setIsArchiving(true);
      const docRef = doc(db, `projects/${projectId}/documents/${document.id}`);
      const restoreStatus = (document.mode === 'general_record' || document.documentType === 'general_record') ? 'record' : 'pending_signatures';
      await updateDoc(docRef, {
        status: restoreStatus,
        archivedAt: null,
        archivedBy: null
      });
      setDocuments(prev => {
        const next = prev.map(d => d.id === document.id ? { ...d, status: restoreStatus, archivedAt: null, archivedBy: null } : d);
        documentsRef.current = next;
        return next;
      });
      const archivedCount = documents.filter(d => d.status === 'archived').length;
      if (archivedCount <= 1) setSelectedFolderId('all');
    } catch (error) {
      console.error('Error unarchiving document:', error);
      alert('Error restoring document');
    } finally {
      setIsArchiving(false);
    }
  };

  // Get recipients from document (for existing documents)
  const getRecipientsFromDocument = (document) => {
    console.log('Getting recipients from document:', document);
    console.log('Document recipients:', document.recipients);
    
    if (document.recipients && document.recipients.length > 0) {
      console.log('Returning stored recipients:', document.recipients);
      
      // Refresh recipients with current team data to get latest phone numbers
      const refreshedRecipients = document.recipients.map(storedRecipient => {
        // Find the current team member data
        const currentTeamMember = teams
          .flatMap(team => team.members)
          .find(member => member.id === storedRecipient.id || member.email === storedRecipient.email);
        
        if (currentTeamMember) {
          // Update with fresh data while preserving document-specific info
          return {
            ...storedRecipient,
            name: currentTeamMember.name,
            email: currentTeamMember.email,
            phone: currentTeamMember.phone,
            authMethod: currentTeamMember.authMethod,
            photoURL: currentTeamMember.photoURL
          };
        }
        
        // If no current team member found, return stored data
        return storedRecipient;
      });
      
      console.log('Refreshed recipients with current team data:', refreshedRecipients);
      return refreshedRecipients;
    }
    console.log('No stored recipients found');
    return [];
  };

  // Check if current user is a recipient for this document
  const isCurrentUserRecipient = (document) => {
    if (!currentUser || !document.recipients) return false;
    return document.recipients.some(recipient => 
      recipient.id === currentUser.id || recipient.email === currentUser.email
    );
  };

  // Check if current user has already signed the document
  const hasCurrentUserSigned = (document) => {
    if (!currentUser) return false;
    
    // First check if document is completed (webhook might have updated this)
    if (document.status === 'completed') {
      console.log('Document is completed, user has signed');
      return true;
    }
    
    // Check if we have docusealSubmitters data
    if (document.docusealSubmitters && document.docusealSubmitters.length > 0) {
      const userSubmitter = document.docusealSubmitters.find(s => 
        s.email === currentUser.email
      );
      
      // Debug logging
      console.log('hasCurrentUserSigned check with submitters:', {
        documentId: document.id,
        currentUserEmail: currentUser.email,
        docusealSubmitters: document.docusealSubmitters,
        userSubmitter,
        hasSigned: userSubmitter && userSubmitter.status === 'completed'
      });
      
      return userSubmitter && userSubmitter.status === 'completed';
    }
    
    // Fallback: check if current user is in recipients and all have signed
    if (document.recipients && document.completedSigners && document.totalSigners) {
      const isRecipient = document.recipients.some(r => 
        r.email === currentUser.email || r.id === currentUser.id
      );
      const allSigned = document.completedSigners === document.totalSigners;
      
      console.log('hasCurrentUserSigned fallback check:', {
        documentId: document.id,
        currentUserEmail: currentUser.email,
        isRecipient,
        completedSigners: document.completedSigners,
        totalSigners: document.totalSigners,
        allSigned
      });
      
      return isRecipient && allSigned;
    }
    
    // Additional fallback: if user is a recipient and document shows some progress, 
    // and we don't have clear data, be more conservative
    if (document.recipients && document.signingProgress > 0) {
      const isRecipient = document.recipients.some(r => 
        r.email === currentUser.email || r.id === currentUser.id
      );
      
      if (isRecipient) {
        console.log('hasCurrentUserSigned: conservative check for recipient with progress:', {
          documentId: document.id,
          currentUserEmail: currentUser.email,
          signingProgress: document.signingProgress,
          completedSigners: document.completedSigners,
          totalSigners: document.totalSigners
        });
        
        // If there's significant progress and user is recipient, assume they might have signed
        // This prevents the progress bar from dropping to 0 temporarily
        return document.signingProgress > 0.5 || (document.completedSigners > 0 && document.completedSigners === document.totalSigners);
      }
    }
    
    console.log('hasCurrentUserSigned: no data available for document', document.id);
    return false;
  };

  // Get signing URL for current user
  const handleSignMyself = async (document) => {
    try {
      if (!document.docusealSubmissionId) {
        alert('No submission found for this document');
        return;
      }

      // Check if we have stored submitter info for this user
      if (document.docusealSubmitters && currentUser) {
        const submitter = document.docusealSubmitters.find(s => s.email === currentUser.email);
        if (submitter && submitter.embed_src) {
          window.open(submitter.embed_src, '_blank');
          // Refresh status more frequently after opening signing URL
          setTimeout(() => refreshDocumentStatus(document.id), 3000);
          setTimeout(() => refreshDocumentStatus(document.id), 8000);
          setTimeout(() => refreshDocumentStatus(document.id), 15000);
          return;
        }
      }

      // Fallback to API call
      const signingUrl = await docusealService.getSigningUrl(
        document.docusealSubmissionId, 
        currentUser.email
      );
      
      if (signingUrl) {
        window.open(signingUrl, '_blank');
        // Refresh status more frequently after opening signing URL
        setTimeout(() => refreshDocumentStatus(document.id), 3000);
        setTimeout(() => refreshDocumentStatus(document.id), 8000);
        setTimeout(() => refreshDocumentStatus(document.id), 15000);
      } else {
        alert('Signing link not available. Please check your email for the signing invitation.');
      }
    } catch (error) {
      console.error('Error getting signing URL:', error);
      alert('Error getting signing URL: ' + error.message);
    }
  };

  // Refresh status for a specific document
  const refreshDocumentStatus = async (documentId) => {
    const document = documents.find(d => d.id === documentId);
    if (!document || !document.docusealSubmissionId) return;
    // DocuSeal sunset: no API calls for legacy docs - use Firestore data only
    return;
  };

  // Current roster who must sign team sign-off (same rules as SigningProgressModal)
  // Get appropriate button for document based on status
    const getDocumentButtons = (document) => {
    const isRecipient = isCurrentUserRecipient(document);
    const hasSigned = hasCurrentUserSigned(document);

    // General documentation: Download only (record keeping, no sign-off)
    if (document.mode === 'general_record' || document.documentType === 'general_record') {
      return [{
        text: 'Download',
        action: () => {
          if (document.fileURL) {
            window.open(document.fileURL, '_blank');
          }
        },
        style: {
          background: '#059669',
          color: '#fff',
          padding: '0.5rem 1rem',
          borderRadius: '0.25rem',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.875rem'
        }
      }];
    }

    // Team sign-off docs: View progress (always) + Download (when signed PDF exists)
    if (document.mode === 'team_sign_off') {
      const hasSignedPdf = document.signedFileURL || document.status === 'fully_signed';
      const buttons = [{
        text: 'View progress',
        action: () => handleViewTeamSignOffProgress(document),
        style: {
          background: 'var(--branding-app-primary)',
          color: '#fff',
          padding: '0.5rem 1rem',
          borderRadius: '0.25rem',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.875rem'
        }
      }];
      if (hasSignedPdf) {
        buttons.unshift({
          text: 'Download',
          action: () => handleDownloadTeamSignOff(document),
          style: {
            background: '#059669',
            color: '#fff',
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }
        });
      }
      if (!isReadOnly && document.teamAssignment === 'selected' && document.status !== 'fully_signed') {
        buttons.push({
          text: 'Add teams',
          action: () => setAddTeamsDocument(document),
          style: {
            background: 'var(--branding-app-surface-panel)',
            color: 'var(--cc-text-primary)',
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            border: '1px solid var(--branding-app-surface-panel-border)',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }
        });
      }
      return buttons;
    }
    
    // Debug logging
    console.log('Document button debug:', {
      id: document.id,
      status: document.status,
      completedSigners: document.completedSigners,
      totalSigners: document.totalSigners,
      docusealSubmissionId: document.docusealSubmissionId,
      completedFileURL: document.completedFileURL,
      isRecipient,
      hasSigned,
      currentUserEmail: currentUser?.email,
      docusealSubmitters: document.docusealSubmitters
    });
    
    const buttons = [];
    const isLegacyDocuSeal = !!document.docusealSubmissionId;

    // If document is completed, show Download (only works if completedFileURL exists for legacy docs)
    if (document.status === 'completed') {
      buttons.push({
        text: 'Download',
        action: () => handleDownloadCompleted(document),
        style: {
          background: '#059669',
          color: '#fff',
          padding: '0.5rem 1rem',
          borderRadius: '0.25rem',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.875rem'
        }
      });
      return buttons;
    }

    // DocuSeal sunset: legacy docs - no Setup signing, no Sign myself, View progress only
    if (isLegacyDocuSeal) {
      buttons.push({
        text: 'View progress',
        action: () => handleViewProgress(document),
        style: {
          background: 'var(--branding-app-primary)',
          color: '#fff',
          padding: '0.5rem 1rem',
          borderRadius: '0.25rem',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.875rem'
        }
      });
      return buttons;
    }
    
    switch (document.status) {
      case 'draft':
        if (!isReadOnly) {
          buttons.push({
            text: 'Setup signing',
            action: () => handleSignDocument(document),
            style: {
              background: 'var(--branding-app-primary)',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }
          });
        }
        break;
      
      case 'sent':
        const allSigned = document.completedSigners === document.totalSigners && document.totalSigners > 0;
        
        if (allSigned) {
          buttons.push({
            text: 'Download',
            action: () => handleDownloadCompleted(document),
            style: {
              background: '#059669',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }
          });
        } else if (isRecipient && !hasSigned) {
          buttons.push({
            text: 'Sign myself',
            action: () => handleSignMyself(document),
            style: {
              background: '#059669',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }
          });
        } else if (isRecipient && hasSigned) {
          buttons.push({
            text: 'Signed ✓',
            action: () => handleViewProgress(document),
            style: {
              background: '#10b981',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }
          });
        }
        
        buttons.push({
          text: 'View progress',
          action: () => handleViewProgress(document),
          style: {
            background: 'var(--branding-app-primary)',
            color: '#fff',
            padding: '0.5rem 1rem',
            borderRadius: '0.25rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }
        });
        break;
      
      default:
        if (!isReadOnly) {
          buttons.push({
            text: 'Setup signing',
            action: () => handleSignDocument(document),
            style: {
              background: 'var(--branding-app-primary)',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }
          });
        }
        break;
    }
    
    return buttons;
  };

  // Handle view progress
  const handleViewProgress = async (document) => {
    setSelectedDocumentForProgress(document);
    setShowProgressModal(true);

    // Immediately refresh progress for this specific document (DocuSeal only)
    if (document.mode !== 'team_sign_off') {
      await refreshDocumentStatus(document.id);
    }
  };

  const refreshTeamSignOffDocument = useCallback(async () => {
    if (!projectId || !selectedDocumentForProgress?.id) return;
    try {
      const docSnap = await getDoc(doc(db, `projects/${projectId}/documents/${selectedDocumentForProgress.id}`));
      if (docSnap.exists()) {
        setSelectedDocumentForProgress({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (err) {
      console.error('Refresh team sign-off doc:', err);
    }
  }, [projectId, selectedDocumentForProgress?.id]);

  const handleViewPDF = (document) => {
    setViewer({
      open: true,
      file: {
        name: document.fileName || document.name,
        url: document.convertedPdfURL || document.fileURL,
        fileType: document.fileType,
      },
    });
  };

  // Team sign-off: download signed or original document
  const handleDownloadTeamSignOff = async (doc) => {
    const url = doc.signedFileURL || doc.fileURL;
    if (!url) {
      alert('No file available for download.');
      return;
    }
    const filename = doc.signedFileURL
      ? `${doc.name || 'document'}_signed.pdf`
      : (doc.fileName || `${doc.name || 'document'}.pdf`);
    try {
      await downloadFile(url, filename);
    } catch {
      window.open(url, '_blank');
    }
  };

  // Team sign-off: view progress (opens SigningProgressModal like Formal signing)
  const handleViewTeamSignOffProgress = (doc) => {
    setSelectedDocumentForProgress(doc);
    setShowProgressModal(true);
  };

  // Team sign-off: add more teams to an existing "Selected teams" document
  const handleSaveAddedTeams = async (documentId, newSelectedTeamIds) => {
    if (isReadOnly) return;
    await updateDoc(doc(db, `projects/${projectId}/documents/${documentId}`), {
      selectedTeams: newSelectedTeamIds
    });
  };

  // Helper function to download a file
  const downloadFile = async (url, filename) => {
    try {
      console.log('Downloading file from:', url);
      
      // Fetch the file
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      
      // Create blob from response
      const blob = await response.blob();
      
      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('File downloaded successfully:', filename);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  };

  // Handle download completed document
  const handleDownloadCompleted = async (document) => {
    try {
      console.log('Starting download for document:', document.id);
      console.log('Document details:', {
        status: document.status,
        docusealSubmissionId: document.docusealSubmissionId,
        completedFileURL: document.completedFileURL,
        completedSigners: document.completedSigners,
        totalSigners: document.totalSigners
      });
      
      // Use existing completedFileURL if available (stored when DocuSeal completed)
      if (document.completedFileURL) {
        console.log('Using existing completedFileURL');
        await downloadFile(document.completedFileURL, document.name);
        return;
      }
      
      // DocuSeal sunset: no API calls - legacy docs without completedFileURL are unavailable
      if (document.docusealSubmissionId) {
        alert('Download unavailable. This document uses legacy signing which is no longer active.');
        return;
      }
      
      alert('Completed document not available yet. Please try again in a few moments.');
      
    } catch (error) {
      console.error('Error downloading document:', error);
      alert(`Error downloading document: ${error.message}`);
    }
  };

  // Handle download partial document (from progress modal)
  const handleDownloadPartial = async (document) => {
    try {
      // DocuSeal sunset: partial download from API no longer available for legacy docs
      if (document.docusealSubmissionId) {
        alert('Partial download unavailable for legacy documents.');
        return;
      }
      alert('No submission found for this document');
    } catch (error) {
      console.error('Error downloading partial document:', error);
      alert(`Error downloading document: ${error.message}`);
    }
  };

  if (loading || !project || userLoading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: 'var(--cc-text-muted)',
        fontSize: '0.875rem'
      }}>
        Loading documents...
      </div>
    );
  }

  return (
    <div className="document-landing">
      {/* Back Button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = '#6b7280';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      <div className="document-header">
        <div>
          <h1 style={{ fontSize: 'clamp(20px, 2.2vw, 22px)', fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>{project?.name}</h1>
          {project?.projectCode && (
            <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
            <strong>Address:</strong> {project?.address}
          </p>
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 24, lineHeight: 1.4 }}>
            <strong>Postcode:</strong> {project?.postcode}
          </p>
        </div>

        <div className="header-actions">
          <div className="document-tabs folder-tabs">
            <button
              type="button"
              className={`document-tab ${selectedFolderId === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedFolderId('all')}
            >
              <Folder size={14} style={{ marginRight: '0.35rem' }} />
              All
            </button>
            {(documentConfig.folders || []).map(f => (
              <button
                key={f.id}
                type="button"
                className={`document-tab ${selectedFolderId === f.id ? 'active' : ''}`}
                onClick={() => setSelectedFolderId(f.id)}
              >
                <Folder size={14} style={{ marginRight: '0.35rem' }} />
                {f.name}
              </button>
            ))}
            <button
              type="button"
              className={`document-tab ${selectedFolderId === 'uncategorised' ? 'active' : ''}`}
              onClick={() => setSelectedFolderId('uncategorised')}
            >
              <Folder size={14} style={{ marginRight: '0.35rem' }} />
              Uncategorised
            </button>
            {hasArchivedDocuments && (
            <button
              type="button"
              className={`document-tab ${selectedFolderId === 'archived' ? 'active' : ''}`}
              onClick={() => setSelectedFolderId('archived')}
            >
              <Archive size={14} style={{ marginRight: '0.35rem' }} />
              Archived
            </button>
            )}
            {!isReadOnly && (
              <button
                type="button"
                className="document-tab folder-add"
                onClick={() => setShowFolderModal(true)}
                title="Manage folders"
              >
                <FolderPlus size={14} />
              </button>
            )}
          </div>
          {selectedFolderId !== 'archived' && (
          <button
            onClick={() => {
              if (isReadOnly) return;
              setUploadTab('rams_and_permits');
              setShowUploadModal(true);
            }}
            className={`upload-button ${isReadOnly ? 'disabled' : ''}`}
            disabled={isReadOnly}
            title={project?.status === 'closed' ? 'Project is closed - no new uploads allowed' : (isReadOnly ? 'You do not have permission to upload documents' : 'Upload document')}
          >
            Upload document
          </button>
          )}
        </div>
      </div>

      {/* Closed project notice */}
      {project?.status === 'closed' && (
        <div className="closed-project-notice">
          <p className="closed-project-text">
            This project is closed and read-only. You can view all documents but cannot upload new ones.
          </p>
        </div>
      )}

      {/* Documents area */}
      <div className="documents-main">
          <div className="documents-toolbar">
            <select
              className="documents-sort-select"
              value={`${documentConfig.sortBy}-${documentConfig.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                saveDocumentConfig({ sortBy, sortOrder });
              }}
            >
              <option value="uploadedAt-desc">Date (newest first)</option>
              <option value="uploadedAt-asc">Date (oldest first)</option>
              <option value="name-asc">Name (A–Z)</option>
              <option value="name-desc">Name (Z–A)</option>
              <option value="signingProgress-asc">Progress (least complete first)</option>
              <option value="signingProgress-desc">Progress (most complete first)</option>
            </select>
          </div>

          <div className="documents-grid">
        {filteredAndSortedDocuments.map((doc) => (
          <div key={doc.id} className="document-card">
            <div className="document-header">
              <div className="document-name-row">
                {editingDocumentId === doc.id ? (
                  <div className="document-name-editing">
                    <input
                      type="text"
                      value={editingDocumentName}
                      onChange={(e) => setEditingDocumentName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelDocumentRename();
                        }
                      }}
                      onBlur={() => renameDocument(doc.id, editingDocumentName)}
                      autoFocus
                      disabled={renamingDocumentId === doc.id}
                      className="document-name-input"
                      aria-label={`Rename ${doc.name}`}
                    />
                    <button
                      type="button"
                      className="document-rename-button document-rename-confirm"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => renameDocument(doc.id, editingDocumentName)}
                      disabled={renamingDocumentId === doc.id}
                      title={`Save ${doc.name}`}
                      aria-label={`Save ${doc.name}`}
                    >
                      <Check size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="document-name">{doc.name}</h3>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="document-rename-button"
                        onClick={() => startDocumentRename(doc)}
                        title={`Rename ${doc.name}`}
                        aria-label={`Rename ${doc.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <p className="document-filename">
              {doc.fileName}
            </p>
            
            <div className="document-tags">
                {/* General records: no status badge; RAMS: show signing status */}
                {!(doc.documentType === 'general_record' || doc.mode === 'general_record') && doc.status !== 'record' && ((doc.mode === 'team_sign_off' && doc.status === 'fully_signed') || doc.mode !== 'team_sign_off') && (
                <span className={`document-status status-${doc.status}`}>
                  {doc.mode === 'team_sign_off'
                    ? (doc.status === 'fully_signed' ? 'Fully signed' : doc.status)
                    : (doc.status === 'draft' ? 'Draft' :
                       doc.status === 'sent' ? 'Sent for signing' :
                       doc.status === 'completed' ? 'Completed' :
                       doc.status)}
                  {doc.status === 'sent' && doc.signingStatus === 'active' && (
                    <span className="pulse-indicator" />
                  )}
                </span>
                )}
              
              {/* Show recipient count - only for RAMS */}
              {doc.documentType !== 'general_record' && doc.mode !== 'general_record' && doc.recipients && doc.recipients.length > 0 && (
                <span className="recipient-count">
                  {doc.recipients.length} recipient{doc.recipients.length > 1 ? 's' : ''}
                </span>
              )}
              
              {/* Show if current user is a recipient - only for RAMS */}
              {doc.documentType !== 'general_record' && doc.mode !== 'general_record' && isCurrentUserRecipient(doc) && (
                <span className="user-included">
                  You included
                </span>
              )}
              
              {/* Show orphaned template warning */}
              {doc.docusealTemplateId && !doc.docusealSubmissionId && (
                <span className="template-warning">
                  <span className="warning-dot" />
                  Template Only
                </span>
              )}
            </div>
            
            {/* Progress bar for DocuSeal documents - skip for general records */}
            {doc.documentType !== 'general_record' && doc.mode !== 'general_record' && doc.docusealSubmissionId && (
              <div className="progress-container">
                <div className="progress-header">
                  <span className="progress-text">
                    {doc.completedSigners || 0} of {doc.totalSigners || (doc.recipients ? doc.recipients.length : 0)} signed
                  </span>
                  <span className="progress-percentage">
                    {doc.signingProgress ? Math.round(doc.signingProgress * 100) : 0}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{
                      width: `${doc.signingProgress ? Math.round(doc.signingProgress * 100) : 0}%`
                    }}
                  />
                </div>
              </div>
            )}
            {/* Progress bar for Team sign-off (dynamic total from current teams + PMs) - skip for general records */}
            {doc.documentType !== 'general_record' && doc.mode === 'team_sign_off' && (() => {
              const { signed, total } = getTeamSignOffProgress(doc, teams, project);
              const pct = total > 0 ? Math.min(100, Math.round((signed / total) * 100)) : 0;
              return (
              <div className="progress-container">
                <div className="progress-header">
                  <span className="progress-text">
                    {signed} of {total} signed
                  </span>
                  <span className="progress-percentage">
                    {pct}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );})()}

            {doc.mode === 'team_sign_off' && doc.teamAssignment === 'selected' && doc.selectedTeams && doc.selectedTeams.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2, marginBottom: 4 }}>
                {teams
                  .filter(team => doc.selectedTeams.includes(team.id))
                  .map(team => (
                    <span key={team.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--cc-text-muted)' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: team.color, border: '1px solid var(--branding-app-surface-panel-border)', flexShrink: 0 }} />
                      {team.name}
                    </span>
                  ))}
              </div>
            )}

            <div className="document-actions">
              {(() => {
                const buttons = getDocumentButtons(doc);
                return buttons.map((button, index) => (
                  <button
                    key={index}
                    onClick={button.action}
                    className={`action-button ${button.text.toLowerCase().replace(/\s+/g, '-')}`}
                    style={button.style}
                  >
                    {button.text}
                  </button>
                ));
              })()}
              
              <button
                onClick={() => handleViewPDF(doc)}
                className="action-button view-pdf"
              >
                View
              </button>
              {!isReadOnly && (doc.status === 'archived' ? (
              <button
                onClick={() => handleUnarchiveDocument(doc)}
                className="action-button archive"
                title="Restore to main list"
              >
                Unarchive
              </button>
              ) : (
              <button
                onClick={() => handleArchiveDocument(doc)}
                className="action-button archive"
                title="Archive document"
              >
                Archive
              </button>
              ))}
              {!isReadOnly && (
                <select
                  className="action-button move-folder-select"
                  value={doc.folderId || ''}
                  onChange={(e) => moveDocumentToFolder(doc.id, e.target.value || null)}
                  onClick={(e) => e.stopPropagation()}
                  title="Move to folder"
                >
                  <option value="">Uncategorised</option>
                  {(documentConfig.folders || []).map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            {/* Upload type tabs inside modal */}
            <div className="document-tabs" style={{ marginBottom: '1.5rem' }}>
              <button
                type="button"
                className={`document-tab ${uploadTab === 'rams_and_permits' ? 'active' : ''}`}
                onClick={() => { setUploadTab('rams_and_permits'); setUploadFile(null); setDocumentName(''); }}
              >
                RAMS & Permits
              </button>
              <button
                type="button"
                className={`document-tab ${uploadTab === 'general_record' ? 'active' : ''}`}
                onClick={() => { setUploadTab('general_record'); setUploadFile(null); setDocumentName(''); }}
              >
                General Documentation
              </button>
            </div>

            <p style={{ fontSize: '0.75rem', color: 'var(--cc-text-muted)', margin: '0 0 1rem 0' }}>
              Tip: Use the CrewChain mobile app to scan documents with edge detection and automatic correction.
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Document name
              </label>
              <input
                type="text"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  boxSizing: 'border-box',
                }}
                placeholder="Enter document name"
              />

            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Folder
              </label>
              <select
                value={uploadFolderId || ''}
                onChange={(e) => setUploadFolderId(e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Uncategorised</option>
                {(documentConfig.folders || []).map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            {/* Team assignment + mandatory - only for RAMS & Permits */}
            {uploadTab === 'rams_and_permits' && (
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Assign to teams</label>
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="teamAssignment"
                        checked={teamAssignment === 'all'}
                        onChange={() => setTeamAssignment('all')}
                      />
                      <span>All teams</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="teamAssignment"
                        checked={teamAssignment === 'selected'}
                        onChange={() => setTeamAssignment('selected')}
                      />
                      <span>Selected teams only</span>
                    </label>
                  </div>
                  {teamAssignment === 'all' && (
                    <p style={{ fontSize: '0.875rem', color: 'var(--cc-text-muted)', marginTop: '0.5rem' }}>
                      Everyone on the project must sign, including any teams added later.
                    </p>
                  )}
                  {teamAssignment === 'selected' && (
                    <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {teams.map(team => (
                        <label
                          key={team.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            background: teamSignOffSelectedTeamIds.includes(team.id) ? '#f0f9ff' : 'var(--branding-app-surface-panel)',
                            border: `1px solid ${teamSignOffSelectedTeamIds.includes(team.id) ? '#0ea5e9' : '#e2e8f0'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={teamSignOffSelectedTeamIds.includes(team.id)}
                            onChange={() => {
                              setTeamSignOffSelectedTeamIds(prev =>
                                prev.includes(team.id)
                                  ? prev.filter(id => id !== team.id)
                                  : [...prev, team.id]
                              );
                            }}
                          />
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: team.color || 'var(--branding-app-primary)' }} />
                          <span>{team.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={mandatoryBeforeSignIn}
                    onChange={(e) => setMandatoryBeforeSignIn(e.target.checked)}
                  />
                  <span>Mandatory before site check-in</span>
                </label>
                <p style={{ fontSize: '0.875rem', color: 'var(--cc-text-muted)' }}>
                  Team members must sign this document before they can check in on site.
                </p>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Signing sheet
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {[
                      { value: 'basic', label: 'Basic', description: 'Name, signature, date.' },
                      { value: 'detailed', label: 'Detailed', description: 'Adds company and job title.' },
                      { value: 'full_induction', label: 'Full induction', description: 'Adds mobile number and emergency contact, plus a CSCS/CPCS/ECS card if they have one.' },
                    ].map(option => (
                      <label
                        key={option.value}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          border: `1px solid ${signOffTemplate === option.value ? 'var(--branding-app-primary)' : '#d1d5db'}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          background: signOffTemplate === option.value ? 'var(--branding-app-primary-light, #f0f9ff)' : 'transparent',
                        }}
                      >
                        <input
                          type="radio"
                          name="signOffTemplate"
                          value={option.value}
                          checked={signOffTemplate === option.value}
                          onChange={() => setSignOffTemplate(option.value)}
                          style={{ marginTop: '0.2rem' }}
                        />
                        <span>
                          <span style={{ display: 'block', fontWeight: 500 }}>{option.label}</span>
                          <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--cc-text-muted)' }}>{option.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Signing sheet title <span style={{ fontSize: '0.875rem', color: 'var(--cc-text-muted)', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={signOffTitle}
                    onChange={(e) => setSignOffTitle(e.target.value.slice(0, 60))}
                    placeholder="e.g. Acknowledgement of Method Statement"
                    maxLength={60}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box',
                    }}
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--cc-text-muted)', marginTop: '0.25rem' }}>
                    Appears at the top of the sign-off sheet. {60 - signOffTitle.length} characters remaining.
                  </p>
                </div>
              </div>
            )}
            
            {/* File Upload Section */}
            {!uploadFile ? (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Select file <span style={{ fontSize: '0.875rem', color: 'var(--cc-text-muted)', fontWeight: 400 }}>
                    (PDF or Word – .pdf, .doc, .docx)
                  </span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      id="file-upload"
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        zIndex: 2
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        border: '2px dashed #d1d5db',
                        borderRadius: '0.5rem',
                        backgroundColor: 'var(--branding-app-surface-panel)',
                        color: 'var(--cc-text-muted)',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: '48px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--branding-app-primary)';
                        e.currentTarget.style.backgroundColor = '#eff6ff';
                        e.currentTarget.style.color = '#1d4ed8';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.backgroundColor = 'var(--branding-app-surface-panel)';
                        e.currentTarget.style.color = '#64748b';
                      }}
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7,10 12,15 17,10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span>
                        Click to select PDF or Word file (.pdf, .doc, .docx)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* File Selected Display */
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Selected document
                </label>
                <div style={{
                  background: 'var(--branding-app-surface-panel)',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  {/* File Thumbnail / Image Preview */}
                  <div style={{
                    width: uploadFile.type.startsWith('image/') ? '200px' : '80px',
                    height: uploadFile.type.startsWith('image/') ? '200px' : '100px',
                    background: 'var(--cc-card-bg)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    border: '1px solid var(--branding-app-surface-panel-border)'
                  }}>
                    {uploadFile.type === 'application/pdf' ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        background: 'var(--cc-card-bg)'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <iframe
                            key={uploadFile.name + uploadFile.size}
                            src={`${filePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              borderRadius: '6px',
                              transform: 'scale(1.02)',
                              transformOrigin: 'center',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                            title="Document Preview"
                            onLoad={(e) => {
                              // Make the iframe content unselectable
                              try {
                                const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
                                if (iframeDoc.body) {
                                  iframeDoc.body.style.userSelect = 'none';
                                  iframeDoc.body.style.webkitUserSelect = 'none';
                                  iframeDoc.body.style.mozUserSelect = 'none';
                                  iframeDoc.body.style.msUserSelect = 'none';
                                  iframeDoc.body.style.pointerEvents = 'none';
                                }
                              } catch (error) {
                                // Cross-origin restrictions may prevent this
                                console.log('Could not modify iframe content for security reasons');
                              }
                            }}
                          />
                        </div>
                        {/* PDF overlay with icon */}
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          background: 'rgba(239, 68, 68, 0.9)',
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          lineHeight: 1,
                          zIndex: 1
                        }}>
                          PDF
                        </div>
                      </div>
                    ) : uploadFile.type.startsWith('image/') ? (
                      <img
                        src={filePreviewUrl}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '6px'
                        }}
                      />
                    ) : uploadFile.name.toLowerCase().endsWith('.doc') || uploadFile.name.toLowerCase().endsWith('.docx') ? (
                      <div style={{
                        fontSize: '20px',
                        color: 'var(--branding-app-primary)',
                        fontWeight: '600'
                      }}>
                        DOC
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '20px',
                        color: 'var(--cc-text-muted)',
                        fontWeight: '600'
                      }}>
                        File
                      </div>
                    )}
                  </div>
                  
                  {/* File Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--cc-text-primary)',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {uploadFile.name}
                    </div>
                    
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--cc-text-muted)',
                      marginBottom: '4px'
                    }}>
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    {(uploadFile.name.toLowerCase().endsWith('.doc') || uploadFile.name.toLowerCase().endsWith('.docx')) && uploadTab === 'rams_and_permits' && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--cc-text-muted)', marginBottom: '8px', marginTop: 0 }}>
                        This will be converted to PDF for signing.
                      </p>
                    )}
                    
                    <button
                      onClick={() => {
                        setUploadFile(null);
                        setDocumentName('');
                      }}
                      style={{
                        background: 'var(--cc-badge-bg)',
                        color: 'var(--cc-text-primary)',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e5e7eb';
                        e.target.style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                    >
                      Change file
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {(() => {
              const teamSignOffValid = uploadTab === 'rams_and_permits'
                ? (teamAssignment === 'all' || teamSignOffSelectedTeamIds.length > 0)
                : true;
              const hasFileOrPhotos = !!uploadFile;
              const canUpload = hasFileOrPhotos && documentName.trim() && !uploading && teamSignOffValid;
              const uploadButtonLabel = uploading ? 'Uploading...' :
                !documentName.trim() ? 'Enter document name' :
                !hasFileOrPhotos ? 'Select file or add photos' :
                uploadTab === 'rams_and_permits' && !teamSignOffValid ? 'Select at least one team' :
                'Upload document';
              return (
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setDocumentName('');
                  setTeamAssignment('all');
                  setTeamSignOffSelectedTeamIds([]);
                  setMandatoryBeforeSignIn(false);
                }}
                style={{
                  background: 'none',
                  color: 'var(--cc-text-muted)',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={uploadTab === 'general_record' ? handleUploadGeneralDocument : handleUploadDocument}
                disabled={!canUpload}
                style={{
                  background: canUpload ? 'var(--branding-app-primary)' : '#cbd5e1',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  border: 'none',
                  cursor: canUpload ? 'pointer' : 'not-allowed',
                  opacity: canUpload ? 1 : 0.6
                }}
              >
                {uploadButtonLabel}
              </button>
            </div>
            );
            })()}
          </div>
        </div>
      )}

      

      {showFolderModal && (
        <FolderManagementModal
          folders={documentConfig.folders}
          onClose={() => setShowFolderModal(false)}
          onAdd={addFolder}
          onRename={renameFolder}
          onDelete={deleteFolder}
        />
      )}

      {addTeamsDocument && (
        <AddTeamsModal
          document={addTeamsDocument}
          teams={teams}
          onClose={() => setAddTeamsDocument(null)}
          onSave={(newSelectedTeamIds) => handleSaveAddedTeams(addTeamsDocument.id, newSelectedTeamIds)}
        />
      )}

      {/* Signing Progress Modal */}
      {showProgressModal && selectedDocumentForProgress && (
        <SigningProgressModal
          document={selectedDocumentForProgress}
          project={project}
          teams={teams}
          isOpen={showProgressModal}
          onClose={() => {
            setShowProgressModal(false);
            setSelectedDocumentForProgress(null);
          }}
          onDownload={
            selectedDocumentForProgress.mode === 'team_sign_off'
              ? () => handleDownloadTeamSignOff(selectedDocumentForProgress)
              : () => handleDownloadPartial(selectedDocumentForProgress)
          }
          onRefresh={
            selectedDocumentForProgress.mode === 'team_sign_off'
              ? refreshTeamSignOffDocument
              : undefined
          }
        />
      )}

      <DrawingViewer
        open={viewer.open}
        onClose={() => setViewer({ open: false, file: null })}
        file={viewer.file}
      />

              {/* Signing Setup Modal */}
        {showSigningSetupModal && selectedDocument && (
          <SigningSetupModal
            document={selectedDocument}
            recipients={getRecipientsFromDocument(selectedDocument).map(recipient => {
              // Ensure PM context is properly set for recipients
              if (currentUser && (recipient.id === currentUser.id || recipient.email === currentUser.email)) {
                return { ...recipient, isPM: true };
              }
              return recipient;
            })}
            isOpen={showSigningSetupModal}
            onClose={() => setShowSigningSetupModal(false)}
          />
        )}
    </div>
  );
}

export default withProjectPermissions(DocumentLanding, { requireEdit: true });